/**
 * useScheduledEvents - Shared state hook for the Calendar + Map views.
 *
 * Both pages need the same primitives:
 *   - Filter state (date range + four multi-selects)
 *   - The current filtered event list (server-fetched via
 *     scheduledEventsService)
 *   - Loading + error flags for the inline UI states
 *   - Option lists for the four MultiSelectFilters, derived from the
 *     events themselves so the user only picks values that actually
 *     match something in the current scope.
 *
 * Centralizing this keeps the two pages focused on layout — they just
 * render the filters, the visualization, and the popover. Switching
 * between Calendar and Map is therefore a layout swap, not a state
 * migration.
 *
 * The hook re-fetches whenever the filter state changes. There's no
 * debounce here; in practice users change filters one at a time and
 * the endpoint is fast enough that an immediate fetch is fine. If we
 * see thrash later we can add a debounce hop without touching callers.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    scheduledEventsService,
    ScheduledEvent,
    ScheduledEventRecordType,
} from '../services/scheduledEventsService';
import type {
    EventFiltersValue,
} from '../components/organisms/EventFiltersBar';
import type { MultiSelectOption } from '../components/molecules';
import { qk } from '../lib/queryKeys';

/**
 * The default empty filter state — passed to the parent on init and
 * exposed by the hook for "Clear all" reset semantics.
 */
export const EMPTY_EVENT_FILTERS: EventFiltersValue = {
    dateRange: { from: '', to: '' },
    customerIds: new Set<string>(),
    assigneeIds: new Set<string>(),
    cities: new Set<string>(),
    states: new Set<string>(),
    // Default: show both record types. An empty Set is reserved for the
    // "deselect all" state which yields an empty event feed.
    recordTypes: new Set<ScheduledEventRecordType>(['work_order', 'inspection']),
};

/**
 * Return shape — exposes everything the calendar/map pages need.
 */
export interface UseScheduledEventsResult {
    /** Current filter state (controlled — also exposed via setFilters) */
    filters: EventFiltersValue;
    /** Setter for filter updates */
    setFilters: (next: EventFiltersValue) => void;

    /** The current event list (server-filtered) */
    events: ScheduledEvent[];
    /** True while the latest fetch is in flight */
    isLoading: boolean;
    /** Error message from the last failed fetch, or null */
    error: string | null;

    /** Derived option lists for the EventFiltersBar multi-selects */
    customerOptions: MultiSelectOption[];
    assigneeOptions: MultiSelectOption[];
    cityOptions: MultiSelectOption[];
    stateOptions: MultiSelectOption[];

    /** Force a refetch (e.g. after the user clicks "Refresh") */
    refresh: () => void;
}

/**
 * Convert the camelCased filter object into the snake_case wire shape
 * used by scheduledEventsService.list. The service already handles
 * the bracketed array encoding.
 */
function buildServiceParams(filters: EventFiltersValue) {
    /* When the user has narrowed the record-type set (one selected, not
     * both), forward the filter. When both are selected, omit it so the
     * backend's "default both" path runs. The all-deselected case is
     * handled upstream by short-circuiting the fetch entirely. */
    const allRecordTypes: ScheduledEventRecordType[] = ['work_order', 'inspection'];
    const isAllRecordTypes =
        filters.recordTypes.size === allRecordTypes.length &&
        allRecordTypes.every((t) => filters.recordTypes.has(t));

    return {
        from: filters.dateRange.from || undefined,
        to: filters.dateRange.to || undefined,
        assigneeIds: filters.assigneeIds.size
            ? Array.from(filters.assigneeIds).map((s) => Number(s)).filter((n) => !Number.isNaN(n))
            : undefined,
        companyIds: filters.customerIds.size
            ? Array.from(filters.customerIds).map((s) => Number(s)).filter((n) => !Number.isNaN(n))
            : undefined,
        cities: filters.cities.size ? Array.from(filters.cities) : undefined,
        states: filters.states.size ? Array.from(filters.states) : undefined,
        recordTypes: isAllRecordTypes ? undefined : Array.from(filters.recordTypes),
    };
}

export function useScheduledEvents(): UseScheduledEventsResult {
    const [filters, setFilters] = useState<EventFiltersValue>(EMPTY_EVENT_FILTERS);

    /* Service params are plain (arrays/strings) and serialize cleanly into the
     * query key — unlike the raw filter object, whose Sets would hash to {}.
     * recordTypes.size is folded into the key so the "all deselected" state
     * (which yields an empty feed) is cached distinctly from "all selected". */
    const params = buildServiceParams(filters);
    const recordTypeCount = filters.recordTypes.size;

    const query = useQuery({
        queryKey: [...qk.scheduledEvents.lists(), { params, recordTypeCount }],
        queryFn: async (): Promise<ScheduledEvent[]> => {
            /* If the user deselected every record type, skip the request and
             * render an empty feed. */
            if (recordTypeCount === 0) return [];
            const res = await scheduledEventsService.list(params);
            /* api.ts wraps responses in ApiResponse<T> and the server wraps
             * payloads in { data: ... }; both are camelized by the api client. */
            return ((res.data as any)?.data ?? []) as ScheduledEvent[];
        },
    });

    const events = useMemo(() => query.data ?? [], [query.data]);
    const isLoading = query.isLoading;
    const error = query.error ? (query.error as Error).message : null;

    /* Derive filter options from the current event list. The lists
     * are deduped and alphabetized so the multi-select UI is stable. */
    const customerOptions: MultiSelectOption[] = useMemo(() => {
        const map = new Map<string, string>();
        for (const e of events) {
            if (e.companyId && e.companyName) {
                map.set(String(e.companyId), e.companyName);
            }
        }
        return Array.from(map.entries())
            .map(([value, label]) => ({ value, label }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [events]);

    const assigneeOptions: MultiSelectOption[] = useMemo(() => {
        const map = new Map<string, string>();
        for (const e of events) {
            for (const a of e.assignees) {
                if (a.id && a.name) map.set(String(a.id), a.name);
            }
        }
        return Array.from(map.entries())
            .map(([value, label]) => ({ value, label }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [events]);

    const cityOptions: MultiSelectOption[] = useMemo(() => {
        const set = new Set<string>();
        for (const e of events) if (e.city) set.add(e.city);
        return Array.from(set)
            .sort()
            .map((c) => ({ value: c, label: c }));
    }, [events]);

    const stateOptions: MultiSelectOption[] = useMemo(() => {
        const set = new Set<string>();
        for (const e of events) if (e.state) set.add(e.state);
        return Array.from(set)
            .sort()
            .map((s) => ({ value: s, label: s }));
    }, [events]);

    return {
        filters,
        setFilters,
        events,
        isLoading,
        error,
        customerOptions,
        assigneeOptions,
        cityOptions,
        stateOptions,
        refresh: () => {
            query.refetch();
        },
    };
}
